import React from 'react';
import { Button } from '../components/ui/button';
import { ArrowRight, Play, Calendar, Clock, MapPin } from 'lucide-react';
import { site } from '../data/mock';
import { useI18n } from '../i18n/I18nProvider';
import { useEvent, calendarEvent } from '../i18n/EventProvider';
import AddToCalendar from './AddToCalendar';

export default function Hero() {
  const { t, lang } = useI18n();
  const ev = calendarEvent(useEvent().event, lang);
  return (
    <section id="top" className="relative min-h-screen w-full overflow-hidden">
      <div className="absolute inset-0">
        <img src={site.heroImage} alt="Planted Ministries riverfront city" className="w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-36 lg:pt-44 pb-20">
        <div className="max-w-3xl">
          <div className="fade-up inline-flex items-center gap-2 border border-[#d4a63d]/50 bg-[#1f3a2a]/40 text-[#d4a63d] text-xs uppercase tracking-wider-xtra px-4 py-2 rounded-full">
            <span className="w-2 h-2 bg-[#d4a63d] rounded-full pulse-dot"></span>
            {t.hero.badge}
          </div>

          <h1 className="fade-up delay-1 font-serif-display text-[#f5efe3] mt-6 text-5xl md:text-6xl lg:text-7xl leading-[1.02]">
            {t.hero.title_1}<br />
            <span className="italic text-[#e8c87a]">{t.hero.title_2}</span>
          </h1>

          <p className="fade-up delay-2 mt-7 text-[#f5efe3]/85 text-lg lg:text-xl max-w-2xl leading-relaxed">{t.hero.sub}</p>

          <div className="fade-up delay-3 mt-9 flex flex-wrap items-center gap-4">
            <Button asChild className="bg-[#d4a63d] hover:bg-[#b88a2a] text-[#1f3a2a] rounded-full h-12 px-7 font-medium">
              <a href="#rsvp" className="inline-flex items-center gap-2">{t.hero.cta} <ArrowRight size={18} /></a>
            </Button>
            {ev && <AddToCalendar event={ev} variant="dark" />}
            <Button asChild variant="ghost" className="text-[#f5efe3] hover:text-[#d4a63d] hover:bg-transparent rounded-full h-12 px-5">
              <a href="#watch" className="inline-flex items-center gap-2"><Play size={16} /> {t.hero.watchCta}</a>
            </Button>
          </div>

          <div className="fade-up delay-4 mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
            <HeroStat icon={<Calendar size={18} />} title={t.hero.date} sub={t.hero.dateSub} />
            <HeroStat icon={<Clock size={18} />} title={t.hero.time} sub={t.hero.timeSub} />
            <HeroStat icon={<MapPin size={18} />} title={t.hero.coffee} sub={t.hero.coffeeSub} />
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#f5efe3]/80">
        <span className="text-[10px] tracking-wider-xtra uppercase">{t.hero.scroll}</span>
        <span className="w-px h-10 bg-[#f5efe3]/50 scroll-line"></span>
      </div>
    </section>
  );
}

function HeroStat({ icon, title, sub }) {
  return (
    <div className="flex items-center gap-3 bg-[#1f3a2a]/40 border border-[#f5efe3]/15 rounded-xl px-4 py-3 backdrop-blur-sm">
      <span className="text-[#d4a63d]">{icon}</span>
      <div>
        <div className="text-[#f5efe3] text-sm font-medium leading-tight">{title}</div>
        <div className="text-[#f5efe3]/60 text-xs">{sub}</div>
      </div>
    </div>
  );
}
